import React, { useMemo } from 'react'

import { useRow } from '../useRow'

import { Editor, Cell, Split, Indicator, CurrentSplitSelector } from '@aytm/app/insights/dashboard/components/ui'

import usePool from '@duckness/use-pool'
import InsightsDashboardPool from '@aytm/app/insights/dashboard/duckness/InsightsDashboardPool'

import { stateSelector, shouldUpdate } from './selectors'

import useValue from '../useValue'
import { isRowAveragesUid } from '@aytm/app/insights/dashboard/lib/config'
import CheckBox from '@aytm/lib/rfd-ui/components/basic/check-box'
import { poolDispatch } from '@aytm/app/insights/dashboard/duckness/poolDispatch'
import { makeToggleFilteredRowAveragesFilterAction } from '@aytm/app/insights/dashboard/duckness/ducks/FiltersDuck/FiltersDuck.actionCreators'

const halfEmHeightStyle = { height: '0.5em', flex: '0 0 0.5em' }

const handleToggleFilteredRowAverages = () => {
  poolDispatch(makeToggleFilteredRowAveragesFilterAction())
}

const useSplits = props => {
  const { subjects, dataFiltered, splits, showSigtest, currentSplit, trackTarget, filteredRowAverages, isSharedView } =
    usePool(InsightsDashboardPool, stateSelector, shouldUpdate)

  /** Items list */
  const items = useMemo(() => props.items || [], [props.items])

  /** Editor state object */
  const editor = useMemo(() => props.editor || null, [props.editor])

  /** Subjects not in items */
  const missedSubjects = useMemo(
    () =>
      Object.keys(subjects || {}).reduce(
        (acc, uid) => (items.some(item => uid === item.uid) ? acc : Object.assign(acc, { [uid]: subjects[uid] })),
        {}
      ),
    [items, subjects]
  )

  /** getColumnData function */
  const { getColumnData } = useValue()

  const { getRow } = useRow({ items, editor })

  return useMemo(
    () => [
      getRow(
        ({ key }) => (
          <CurrentSplitSelector
            key={key}
            splits={splits}
            currentSplit={currentSplit}
            onCurrentSplitChange={props.onCurrentSplitChange}
          />
        ),
        ({ key }) => (
          <Cell key={key}>
            <Editor rowType={'error'} editor={editor} />
          </Cell>
        ),
        ({ key, item, itemIndex, isExtraColumn, isEditing }) => {
          if (isExtraColumn && !currentSplit && 'entity' === trackTarget) {
            return isEditing ? (
              <div key={key} />
            ) : (
              <Cell align={'bottom'}>
                <Editor
                  rowType={'selector'}
                  subjectType={props.subjectType}
                  subjects={missedSubjects}
                  onItemAdd={props.onItemAdd}
                />
                <div style={halfEmHeightStyle} />
              </Cell>
            )
          } else {
            return currentSplit ? (
              <Split key={key} index={itemIndex} items={items} />
            ) : (
              <>
                <Indicator
                  key={`${key}:${filteredRowAverages}`}
                  index={itemIndex}
                  items={items}
                  item={item}
                  filtered={dataFiltered}
                  onGetColumnData={getColumnData}
                  showSigtest={showSigtest}
                  {...(isRowAveragesUid(item.uid)
                    ? {
                        children: (
                          <CheckBox
                            className={'insights-portal insights-portal-inline'}
                            label={'Portal wide'}
                            checked={!filteredRowAverages}
                            disabled={isSharedView}
                            onChange={handleToggleFilteredRowAverages}
                            title={
                              'If checked, averages are calculated portal-wide across all columns, even those not shown in the table'
                            }
                          />
                        )
                      }
                    : {})}
                />
              </>
            )
          }
        }
      )
    ],
    [
      getRow,
      splits,
      currentSplit,
      props.onCurrentSplitChange,
      props.subjectType,
      props.onItemAdd,
      editor,
      missedSubjects,
      items,
      filteredRowAverages,
      dataFiltered,
      getColumnData,
      showSigtest,
      isSharedView
    ]
  )
}

export default useSplits
